/**
 * Quote Modal Component
 * Modal for creating and editing quotes with line items
 */

import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@/components/ui";
import { AlertModal } from "@/components/ui/AlertModal/AlertModal";
import styles from "./QuoteModal.module.css";

interface QuoteLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
}

interface Quote {
  id?: string;
  clientId: string;
  clientName: string;
  date: string;
  validUntil: string;
  notes: string;
  lineItems: QuoteLineItem[];
  subtotal: number;
  tax: number;
  total: number;
  pestType?:
    | "Cucarachas"
    | "Ratas"
    | "Ratones"
    | "Hormigas"
    | "Chinches"
    | "Termitas"
    | "Moscas"
    | "Mosquitos"
    | "Avispas"
    | "Pulgas"
    | "Garrapatas"
    | "Otros";
}

interface QuoteModalProps {
  isOpen: boolean;
  quote: Quote | null;
  clients: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSave: (quote: Quote) => void;
}

const defaultLineItem: QuoteLineItem = {
  id: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  total: 0,
};

/**
 * QuoteModal component
 */
export function QuoteModal({
  isOpen,
  quote,
  clients,
  onClose,
  onSave,
}: QuoteModalProps) {
  const defaultDates = useMemo(() => {
    const now = new Date();
    const future = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return {
      date: now.toISOString().split("T")[0],
      validUntil: future.toISOString().split("T")[0],
    };
  }, []);

  const getInitialData = (): Quote => {
    if (quote) {
      return {
        ...quote,
        date: quote.date || defaultDates.date,
        validUntil: quote.validUntil || defaultDates.validUntil,
        lineItems:
          quote.lineItems && quote.lineItems.length > 0
            ? quote.lineItems
            : [{ ...defaultLineItem, id: "1" }],
        notes: quote.notes || "",
      };
    }
    return {
      clientId: "",
      clientName: "",
      date: defaultDates.date,
      validUntil: defaultDates.validUntil,
      notes: "",
      lineItems: [{ ...defaultLineItem, id: "1" }],
      subtotal: 0,
      tax: 0,
      total: 0,
      pestType: undefined,
    };
  };

  const [formData, setFormData] = useState<Quote>(getInitialData);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "error" | "success";
  }>({
    isOpen: false,
    message: "",
    type: "info",
  });

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const calculateLineTotal = (item: QuoteLineItem): number => {
    const subtotal = item.quantity * item.unitPrice;
    const discountAmount = (subtotal * item.discount) / 100;
    return subtotal - discountAmount;
  };

  const calculateTotals = (items: QuoteLineItem[]) => {
    const subtotal = items.reduce(
      (sum, item) => sum + calculateLineTotal(item),
      0,
    );
    const tax = subtotal * 0.21; // IVA 21%
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const updateLineItem = (
    index: number,
    field: keyof QuoteLineItem,
    value: string | number,
  ) => {
    const newLineItems = [...formData.lineItems];
    newLineItems[index] = {
      ...newLineItems[index],
      [field]: value,
    };
    newLineItems[index].total = calculateLineTotal(newLineItems[index]);

    const totals = calculateTotals(newLineItems);
    setFormData({
      ...formData,
      lineItems: newLineItems,
      ...totals,
    });
  };

  const addLineItem = () => {
    const newLineItems = [
      ...formData.lineItems,
      { ...defaultLineItem, id: String(Date.now()) },
    ];
    setFormData({
      ...formData,
      lineItems: newLineItems,
    });
  };

  const removeLineItem = (index: number) => {
    if (formData.lineItems.length === 1) return;
    const newLineItems = formData.lineItems.filter((_, i) => i !== index);
    const totals = calculateTotals(newLineItems);
    setFormData({
      ...formData,
      lineItems: newLineItems,
      ...totals,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validar que la fecha de validez sea posterior a la fecha de emisión
    if (new Date(formData.validUntil) <= new Date(formData.date)) {
      setAlertModal({
        isOpen: true,
        message: "La fecha de validez debe ser posterior a la fecha de emisión",
        type: "warning",
      });
      return;
    }

    // Validar que todas las líneas tengan descripción
    if (formData.lineItems.some((item) => !item.description.trim())) {
      setAlertModal({
        isOpen: true,
        message: "Todas las líneas de servicio deben tener una descripción",
        type: "warning",
      });
      return;
    }

    // Validar que todas las líneas tengan cantidad válida
    if (formData.lineItems.some((item) => item.quantity <= 0)) {
      setAlertModal({
        isOpen: true,
        message: "La cantidad debe ser mayor a 0 en todas las líneas",
        type: "warning",
      });
      return;
    }

    // Validar que todas las líneas tengan precio válido
    if (formData.lineItems.some((item) => item.unitPrice < 0)) {
      setAlertModal({
        isOpen: true,
        message: "El precio unitario no puede ser negativo",
        type: "error",
      });
      return;
    }

    // Validar que haya al menos una línea de servicio
    if (formData.lineItems.length === 0) {
      setAlertModal({
        isOpen: true,
        message: "Debe agregar al menos una línea de servicio",
        type: "warning",
      });
      return;
    }

    const selectedClient = clients.find((c) => c.id === formData.clientId);
    onSave({
      ...formData,
      clientName: selectedClient?.name || "",
    });
  };

  const handleClientChange = (clientId: string) => {
    const selectedClient = clients.find((c) => c.id === clientId);
    setFormData({
      ...formData,
      clientId,
      clientName: selectedClient?.name || "",
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{quote ? "Editar Presupuesto" : "Nuevo Presupuesto"}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Icon name="xCircle" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Header Info */}
          <div className={styles.headerSection}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="clientId">Cliente *</label>
                <select
                  id="clientId"
                  name="clientId"
                  value={formData.clientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  required
                >
                  <option value="">Seleccionar cliente</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="date">Fecha *</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="validUntil">Válido hasta *</label>
                <input
                  type="date"
                  id="validUntil"
                  name="validUntil"
                  value={formData.validUntil}
                  onChange={(e) =>
                    setFormData({ ...formData, validUntil: e.target.value })
                  }
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="pestType">Tipo de Plaga *</label>
                <select
                  id="pestType"
                  name="pestType"
                  value={formData.pestType || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pestType: e.target.value as Quote["pestType"],
                    })
                  }
                  required
                >
                  <option value="">Seleccionar tipo de plaga...</option>
                  <option value="Cucarachas">Cucarachas</option>
                  <option value="Ratas">Ratas</option>
                  <option value="Ratones">Ratones</option>
                  <option value="Hormigas">Hormigas</option>
                  <option value="Chinches">Chinches</option>
                  <option value="Termitas">Termitas</option>
                  <option value="Moscas">Moscas</option>
                  <option value="Mosquitos">Mosquitos</option>
                  <option value="Avispas">Avispas</option>
                  <option value="Pulgas">Pulgas</option>
                  <option value="Garrapatas">Garrapatas</option>
                  <option value="Otros">Otros</option>
                </select>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className={styles.lineItemsSection}>
            <div className={styles.sectionHeader}>
              <h3>Líneas de Servicio</h3>
              <button
                type="button"
                className={styles.addLineButton}
                onClick={addLineItem}
              >
                <Icon name="plus" size={16} />
                Añadir línea
              </button>
            </div>

            <div className={styles.lineItemsTable}>
              <div className={styles.tableHeader}>
                <div className={styles.colDescription}>Descripción</div>
                <div className={styles.colQuantity}>Cant.</div>
                <div className={styles.colPrice}>Precio Unit.</div>
                <div className={styles.colDiscount}>Desc. %</div>
                <div className={styles.colTotal}>Total</div>
                <div className={styles.colActions}></div>
              </div>

              {formData.lineItems.map((item, index) => (
                <div key={item.id} className={styles.lineItem}>
                  <div className={styles.colDescription}>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) =>
                        updateLineItem(index, "description", e.target.value)
                      }
                      placeholder="Ej: Control de plagas mensual"
                      minLength={3}
                      maxLength={200}
                      required
                      title="La descripción debe tener entre 3 y 200 caracteres"
                    />
                  </div>
                  <div className={styles.colQuantity}>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        updateLineItem(
                          index,
                          "quantity",
                          Number(e.target.value),
                        )
                      }
                      min="1"
                      step="1"
                      required
                      title="La cantidad debe ser al menos 1"
                    />
                  </div>
                  <div className={styles.colPrice}>
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) =>
                        updateLineItem(
                          index,
                          "unitPrice",
                          Number(e.target.value),
                        )
                      }
                      min="0.01"
                      step="0.01"
                      required
                      title="El precio debe ser mayor a 0"
                    />
                  </div>
                  <div className={styles.colDiscount}>
                    <input
                      type="number"
                      value={item.discount}
                      onChange={(e) =>
                        updateLineItem(
                          index,
                          "discount",
                          Number(e.target.value),
                        )
                      }
                      min="0"
                      max="100"
                      step="1"
                      title="Descuento entre 0 y 100%"
                    />
                  </div>
                  <div className={styles.colTotal}>
                    <span className={styles.totalAmount}>
                      {calculateLineTotal(item).toFixed(2)} €
                    </span>
                  </div>
                  <div className={styles.colActions}>
                    {formData.lineItems.length > 1 && (
                      <button
                        type="button"
                        className={styles.removeLineButton}
                        onClick={() => removeLineItem(index)}
                        title="Eliminar línea"
                      >
                        <Icon name="xCircle" size={18} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className={styles.totalsSection}>
            <div className={styles.notesColumn}>
              <div className={styles.formGroup}>
                <label htmlFor="notes">Notas / Observaciones</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={4}
                  maxLength={500}
                  placeholder="Información adicional sobre el presupuesto... (máx. 500 caracteres)"
                />
              </div>
            </div>
            <div className={styles.totalsColumn}>
              <div className={styles.totalRow}>
                <span>Subtotal:</span>
                <span>{formData.subtotal.toFixed(2)} €</span>
              </div>
              <div className={styles.totalRow}>
                <span>IVA (21%):</span>
                <span>{formData.tax.toFixed(2)} €</span>
              </div>
              <div className={styles.totalRowFinal}>
                <span>TOTAL:</span>
                <span>{formData.total.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button type="submit" className={styles.saveButton}>
              <Icon name="checkCircle" size={18} />
              {quote ? "Guardar Cambios" : "Crear Presupuesto"}
            </button>
          </div>
        </form>
      </div>

      {/* AlertModal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
}
